import { Component, Inject, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { DialogService } from '../../services/dialog.service';
import { DataDestinationGet } from '../../models/data-destination/data-destination-get.model';

export interface DestinationDialogData {
  title?: string;
  projectId: string;
  datasourceId?: string; // Add datasourceId for API endpoints
  schemaData?: { columns: { name: string; dataType: string }[] } | null; // Schema data for column selection
  existingDestinations?: DataDestinationGet[]; // Existing destinations for validation
  mode?: 'create' | 'view'; // Dialog mode: create new or view existing
  destination?: DataDestinationGet; // Existing destination data for view mode
}

export interface DestinationFormData {
  outputType: string;
  format: string;
  returnFormat?: string; // For API responses
  configuration?: { [key: string]: any };
}

interface OutputOption {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'api' | 'file' | 'database' | 'summary';
}

@Component({
  selector: 'app-set-destination-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    MatCheckboxModule,
    FormsModule
  ],
  templateUrl: './set-destination-dialog.component.html',
  styleUrls: ['./set-destination-dialog.component.scss']
})
export class SetDestinationDialogComponent implements OnInit, OnChanges {
  // Input/Output for inline mode
  @Input() inlineData?: DestinationDialogData;
  @Output() inlineSubmit = new EventEmitter<{ data: DestinationFormData }>();
  @Output() inlineCancel = new EventEmitter<void>();
  @Output() titleChange = new EventEmitter<string>();
  @Output() subtitleChange = new EventEmitter<string>();
  
  // Get data from either inline input or dialog injection
  get effectiveData(): DestinationDialogData {
    return this.inlineData || this.data || { projectId: '' };
  }
  
  // Check if running in inline mode
  get isInlineMode(): boolean {
    return !!this.inlineData;
  }
  
  // Check if dialog is in view mode (viewing existing destination)
  get isViewMode(): boolean {
    return this.effectiveData.mode === 'view' && !!this.effectiveData.destination;
  }
  
  loading = false;
  error: string | null = null;

  formData: DestinationFormData = {
    outputType: '',
    format: '',
    returnFormat: 'json', // Default to JSON for API responses
    configuration: {}
  };

  // Output options organized by category
  outputOptions: OutputOption[] = [
    // API Response (Return to Sender)
    {
      id: 'json-api',
      name: 'JSON',
      description: 'File',
      icon: 'json',
      category: 'api'
    },
    
    // Output to File
    {
      id: 'text-csv-file',
      name: 'Text/CSV',
      description: 'File',
      icon: 'description',
      category: 'file'
    },
    // {
    //   id: 'parquet-file',
    //   name: 'Parquet',
    //   description: 'File',
    //   icon: 'storage',
    //   category: 'file'
    // },
    {
      id: 'json-file',
      name: 'JSON',
      description: 'File',
      icon: 'json',
      category: 'file'
    },
    
    // Output to Database
    // {
    //   id: 'sql-insert',
    //   name: 'SQL',
    //   description: 'Data Insert',
    //   icon: 'storage',
    //   category: 'database'
    // },
    
    // Data Summaries
    // {
    //   id: 'pdf-summary',
    //   name: 'PDF',
    //   description: 'File',
    //   icon: 'picture_as_pdf',
    //   category: 'summary'
    // },
    // {
    //   id: 'html-summary',
    //   name: 'HTML',
    //   description: 'File',
    //   icon: 'web',
    //   category: 'summary'
    // },
    // {
    //   id: 'json-summary',
    //   name: 'JSON',
    //   description: 'File',
    //   icon: 'code',
    //   category: 'summary'
    // }
  ];

  constructor(
    @Optional() public dialogRef: MatDialogRef<SetDestinationDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: DestinationDialogData,
    private dataService: DataService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    // Reset component when inlineData changes
    if (changes['inlineData'] && !changes['inlineData'].firstChange) {
      const currentData = changes['inlineData'].currentValue as DestinationDialogData;
      const previousData = changes['inlineData'].previousValue as DestinationDialogData;
      
      // Check if project ID or datasource ID changed
      const projectIdChanged = currentData?.projectId !== previousData?.projectId;
      const datasourceIdChanged = currentData?.datasourceId !== previousData?.datasourceId;
      
      // Always reset if there's any meaningful change
      if (projectIdChanged || datasourceIdChanged) {
        this.resetComponent();
        this.initializeComponent();
      }
    }
  }
  
  private resetComponent(): void {
    // Reset all component state
    this.formData = {
      outputType: '',
      format: '',
      returnFormat: 'json',
      configuration: {}
    };
    this.loading = false;
    this.error = null;
  }
  
  private initializeComponent(): void {
    // Component initialized - ready to select destination
    // Emit initial title for inline mode
    if (this.isInlineMode) {
      this.emitTitle();
    }
  }
  
  private emitTitle(): void {
    if (this.isInlineMode) {
      this.titleChange.emit(this.getDialogTitle());
      this.subtitleChange.emit(''); // Destination dialog doesn't use subtitle
    }
  }
  
  private getDialogTitle(): string {
    return this.effectiveData.title || 'Set Output';
  }

  // Check if an option is allowed based on existing destinations
  isOptionAllowed(option: OutputOption): boolean {
    if (!this.effectiveData.existingDestinations || this.effectiveData.existingDestinations.length === 0) {
      return true;
    }

    // Check for API Response restriction (only one allowed)
    if (option.category === 'api') {
      const hasApiResponse = this.effectiveData.existingDestinations.some(
        dest => dest.destinationTypeName === 'API Response'
      );
      if (hasApiResponse) {
        return false;
      }
    }

    // Check for duplicate (same destination type and format)
    const formatTypeName = this.mapFormatToFormatType(option.id);
    const typeNameFromCategory = this.mapOutputTypeToDestinationType(option.category);
    
    const isDuplicate = this.effectiveData.existingDestinations.some(
      dest => dest.destinationTypeName === typeNameFromCategory && 
              dest.destinationFormatTypeName === formatTypeName
    );

    return !isDuplicate;
  }

  // Get disabled reason for option
  getDisabledReason(option: OutputOption): string {
    if (!this.effectiveData.existingDestinations || this.effectiveData.existingDestinations.length === 0) {
      return '';
    }

    // Check for API Response restriction
    if (option.category === 'api') {
      const hasApiResponse = this.effectiveData.existingDestinations.some(
        dest => dest.destinationTypeName === 'API Response'
      );
      if (hasApiResponse) {
        return 'Only one API Response output is allowed per project';
      }
    }

    // Check for duplicate
    const formatTypeName = this.mapFormatToFormatType(option.id);
    const typeNameFromCategory = this.mapOutputTypeToDestinationType(option.category);
    
    const isDuplicate = this.effectiveData.existingDestinations.some(
      dest => dest.destinationTypeName === typeNameFromCategory && 
              dest.destinationFormatTypeName === formatTypeName
    );

    if (isDuplicate) {
      return 'This output already exists for this project';
    }

    return '';
  }

  // Check if current selection is API-related
  isApiSelection(): boolean {
    return this.formData.outputType === 'api';
  }

  // Get options by category
  getOptionsByCategory(category: string): OutputOption[] {
    return this.outputOptions.filter(option => option.category === category);
  }

  // Select an output option
  selectOption(option: OutputOption): void {
    // Check if option is allowed
    if (!this.isOptionAllowed(option)) {
      console.warn('Cannot select option:', this.getDisabledReason(option));
      return;
    }

    this.formData.outputType = option.category;
    this.formData.format = option.id;
    
    // Set the return format for API options based on the selected format
    if (option.category === 'api') {
      this.formData.returnFormat = this.mapFormatIdToReturnFormat(option.id);
    }
    
    console.log('Selected option:', option);
  }

  // Map format ID to return format value
  private mapFormatIdToReturnFormat(formatId: string): string {
    switch (formatId) {
      case 'parquet-api':
        return 'parquet';
      case 'json-api':
        return 'json';
      default:
        return 'json'; // Default fallback
    }
  }

  // Check if an option is selected
  isOptionSelected(option: OutputOption): boolean {
    return this.formData.format === option.id;
  }

  // Get category title
  getCategoryTitle(category: string): string {
    const titles: { [key: string]: string } = {
      'api': 'Direct Access',
      'file': 'Store in DQP',
      'database': 'Output to Database',
      'summary': 'Data Summaries'
    };
    return titles[category] || category;
  }

  // Get category subtitle
  getCategorySubtitle(category: string): string {
    const subtitles: { [key: string]: string } = {
      'api': 'Access your cleaned data shortly after cleaning; available for immediate download but not stored long term',
      'file': 'Save your cleaned data to a file that can be downloaded or accessed through View Outputs.',
      'database': 'Store your cleaned data in a database for long-term storage and querying',
      'summary': 'Generate statistical summaries and insights about your cleaned data'
    };
    return subtitles[category] || '';
  }

  // Get category usecase
  getCategoryUsecase(category: string): string {
    const usecases: { [key: string]: string } = {
      'api': 'Ideal for quickly accessing recently cleaned data, when you either don\'t need to keep the cleaned data long-term or want to manage data storage yourself.',
      'file': 'Ideal for keeping track of cleaned datasets in a central location, and accessing data from historical cleaning runs.',
      'database': 'TBD - add use case examples for database outputs',
      'summary': 'TBD - add use case examples for data summaries'
    };
    return usecases[category] || '';
  }

  // Get category icon
  getCategoryIcon(category: string): string {
    const icons: { [key: string]: string } = {
      'api': 'api',
      'file': 'folder',
      'database': 'storage',
      'summary': 'assessment'
    };
    return icons[category] || 'category';
  }

  // Check if can proceed to next step
  canProceed(): boolean {
    return this.formData.format !== '';
  }

  // Map output type to destination type name for API
  private mapOutputTypeToDestinationType(outputType: string): string {
    switch (outputType) {
      case 'api':
        return 'API Response';
      case 'file':
        return 'Output to File';
      case 'database':
        return 'Output to Database';
      case 'summary':
        return 'Data Summary';
      default:
        return 'Unknown';
    }
  }

  // Map format to format type name for API
  private mapFormatToFormatType(format: string): string {
    // Extract the base format from the format ID
    if (format.includes('csv') || format.includes('text-csv')) {
      return 'Csv';
    } else if (format.includes('parquet')) {
      return 'Parquet';
    } else if (format.includes('json')) {
      return 'Json';
    } else if (format.includes('pdf')) {
      return 'Pdf';
    } else if (format.includes('html')) {
      return 'Html';
    } else if (format.includes('sql')) {
      return 'Sql';
    } else {
      return 'Json'; // Default fallback
    }
  }

  onConfirm(): void {
    this.loading = true;

    // Map the form data to the API request format
    const destinationTypeName = this.mapOutputTypeToDestinationType(this.formData.outputType);
    const destinationFormatTypeName = this.mapFormatToFormatType(this.formData.format);
    
    const requestData = {
      projectId: this.effectiveData.projectId,
      destinationTypeName: destinationTypeName,
      destinationFormatTypeName: destinationFormatTypeName,
      settingsJson: null, // No additional settings needed for now
      isEnabled: true,
      autoCreateAllowedPair: false
    };

    console.log('Creating output with request:', requestData);

    // Call the API to create the data destination
    this.dataService.createDataDestination(requestData).subscribe({
      next: (dataDestinationId) => {
        console.log('Output created successfully with ID:', dataDestinationId);
        this.loading = false;
        
        // Handle both dialog and inline modes
        if (this.isInlineMode) {
          this.inlineSubmit.emit({
            data: this.formData
          });
        } else if (this.dialogRef) {
          this.dialogRef.close({
            success: true,
            data: {
              outputType: this.formData.outputType,
              format: this.formData.format
            },
            dataDestinationId: dataDestinationId
          });
        }
      },
      error: (error) => {
        console.error('Error creating data output:', error);
        this.loading = false;
        this.error = error.message || 'Failed to create data output';
      }
    });
  }

  onCancel(): void {
    if (this.isInlineMode) {
      this.inlineCancel.emit();
    } else if (this.dialogRef) {
      this.dialogRef.close({ success: false });
    }
  }

  // Get confirm button text
  getConfirmButtonText(): string {
    if (this.loading) {
      return 'Setting...';
    }
    return 'Set Output';
  }
  
  // Get destination display info for view mode
  getDestinationDisplayInfo(): { icon: string; category: string; format: string } {
    if (!this.effectiveData.destination) {
      return { icon: 'error', category: 'Unknown', format: 'Unknown' };
    }
    
    const dest = this.effectiveData.destination;
    
    // Map destination type to category
    let category = '';
    if (dest.destinationTypeName === 'API Response') {
      category = 'API Response (Return to Sender)';
    } else if (dest.destinationTypeName === 'Output to File') {
      category = 'Output to File';
    } else if (dest.destinationTypeName === 'Output to Database') {
      category = 'Output to Database';
    } else if (dest.destinationTypeName === 'Data Summary') {
      category = 'Data Summaries';
    } else {
      category = dest.destinationTypeName;
    }
    
    // Map format to icon and display name
    let icon = 'description';
    let format = dest.destinationFormatTypeName;
    
    switch (dest.destinationFormatTypeName.toLowerCase()) {
      case 'excel':
        icon = 'description';
        format = 'Excel workbook';
        break;
      case 'csv':
        icon = 'description';
        format = 'Text/CSV';
        break;
      case 'parquet':
        icon = 'storage';
        format = 'Parquet';
        break;
      case 'json':
        icon = 'json';
        format = 'JSON';
        break;
      case 'pdf':
        icon = 'picture_as_pdf';
        format = 'PDF';
        break;
      case 'html':
        icon = 'web';
        format = 'HTML';
        break;
      case 'sql':
        icon = 'storage';
        format = 'SQL';
        break;
    }
    
    return { icon, category, format };
  }
  
  // Handle delete action
  onDelete(): void {
    if (!this.effectiveData.destination) {
      return;
    }
    
    const displayInfo = this.getDestinationDisplayInfo();
    
    this.dialogService.openConfirmDialog(
      'Delete Output',
      `Are you sure you want to delete this output? This will remove the ${displayInfo.format} output from your project.`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed) {
        this.performDelete();
      }
    });
  }
  
  private performDelete(): void {
    if (!this.effectiveData.destination) {
      return;
    }
    
    this.loading = true;
    this.error = null;
    
    this.dataService.deleteDataDestination(this.effectiveData.destination.dataDestinationId).subscribe({
      next: (success) => {
        console.log('Output deleted successfully');
        this.loading = false;
        
        // Close dialog and return success with deletion flag
        if (this.isInlineMode) {
          this.inlineSubmit.emit({
            data: { 
              outputType: 'deleted',
              format: 'deleted'
            }
          });
        } else if (this.dialogRef) {
          this.dialogRef.close({
            success: true,
            deleted: true,
            dataDestinationId: this.effectiveData.destination!.dataDestinationId
          });
        }
      },
      error: (error) => {
        console.error('Error deleting output:', error);
        this.loading = false;
        this.error = error.message || 'Failed to delete output';
      }
    });
  }
}
